import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UploadModal = ({ isOpen, onClose, documentType, onUpload }) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const documentTypes = {
    id: {
      title: 'Identity Document',
      formats: ['JPG', 'PNG', 'PDF'],
      maxSize: '10MB',
      examples: ['Passport', 'Driver\'s License', 'National ID Card']
    },
    address: {
      title: 'Proof of Address',
      formats: ['JPG', 'PNG', 'PDF'],
      maxSize: '10MB',
      examples: ['Utility Bill', 'Bank Statement', 'Government Letter']
    },
    selfie: {
      title: 'Selfie Verification',
      formats: ['JPG', 'PNG'],
      maxSize: '5MB',
      examples: ['Clear face photo', 'Good lighting', 'No filters']
    }
  };

  const currentDoc = documentTypes?.[documentType];

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFileSelect(e?.dataTransfer?.files?.[0]);
    }
  };

  const handleFileSelect = (file) => {
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadProgress(0);

    // Simulate upload progress
    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsUploading(false);
          onUpload(documentType, selectedFile);
          handleClose();
          return 100;
        }
        return prev + 10;
      });
    }, 200);
  };

  const handleClose = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    setIsUploading(false);
    onClose();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Upload {currentDoc?.title}</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            iconName="X"
            iconSize={20}
          />
        </div>

        <div className="p-6">
          <div className="mb-6">
            <h3 className="text-sm font-medium text-foreground mb-2">Document Requirements:</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center space-x-2">
                <Icon name="FileType" size={16} className="text-primary" />
                <span className="text-muted-foreground">
                  Format: {currentDoc?.formats?.join(', ')}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="HardDrive" size={16} className="text-primary" />
                <span className="text-muted-foreground">
                  Max size: {currentDoc?.maxSize}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Shield" size={16} className="text-primary" />
                <span className="text-muted-foreground">Encrypted upload</span>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h4 className="text-sm font-medium text-foreground mb-2">Accepted Documents:</h4>
            <div className="flex flex-wrap gap-2">
              {currentDoc?.examples?.map((example, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-primary/10 text-primary text-xs rounded-full"
                >
                  {example}
                </span>
              ))}
            </div>
          </div>

          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors duration-200 ${
              dragActive
                ? 'border-primary bg-primary/5'
                : selectedFile
                ? 'border-success bg-success/5' :'border-border hover:border-primary/50'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {selectedFile ? (
              <div className="space-y-4">
                <div className="flex items-center justify-center">
                  <Icon name="FileCheck" size={48} className="text-success" />
                </div>
                <div>
                  <p className="text-lg font-medium text-foreground">{selectedFile?.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {formatFileSize(selectedFile?.size)} • {selectedFile?.type}
                  </p>
                </div>
                {isUploading && (
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-center">
                  <Icon name="Upload" size={48} className="text-muted-foreground" />
                </div>
                <div>
                  <p className="text-lg font-medium text-foreground">
                    Drag and drop your file here
                  </p>
                  <p className="text-sm text-muted-foreground">
                    or click to browse from your device
                  </p>
                </div>
              </div>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept={currentDoc?.formats?.map(f => `.${f?.toLowerCase()}`)?.join(',')}
            onChange={(e) => handleFileSelect(e?.target?.files?.[0])}
          />

          <div className="flex justify-center mt-4">
            <Button
              variant="outline"
              onClick={() => fileInputRef?.current?.click()}
              iconName="FolderOpen"
              iconPosition="left"
              iconSize={16}
            >
              Browse Files
            </Button>
          </div>

          {documentType === 'selfie' && (
            <div className="mt-6 p-4 bg-muted rounded-lg">
              <div className="flex items-start space-x-3">
                <Icon name="Camera" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-foreground mb-1">Selfie Guidelines:</h4>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li>• Face clearly visible and well-lit</li>
                    <li>• Look directly at the camera</li>
                    <li>• Remove glasses and hats if possible</li>
                    <li>• Plain background preferred</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!selectedFile || isUploading}
            loading={isUploading}
            iconName="Upload"
            iconPosition="left"
            iconSize={16}
          >
            {isUploading ? `Uploading... ${uploadProgress}%` : 'Upload Document'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;